import type {getRoundedCalculations} from "@client/calculations";
import {EYEBROW, HAIRLINE} from "@client/components/styles";
import {classNames} from "@reactivated";
import React from "react";

type Calculations = ReturnType<typeof getRoundedCalculations>;
type Metric = keyof Omit<Calculations, "summary">;

const SUB_MACROS: [Metric, string][] = [
    ["saturated_fat", "Saturated fat"],
    ["monounsaturated_fat", "Monounsaturated"],
    ["polyunsaturated_fat", "Polyunsaturated"],
    ["trans_fat", "Trans fat"],
    ["sugars", "Total sugars"],
    ["added_sugars", "Added sugars"],
    ["fiber", "Fiber"],
    ["net_carbs", "Net carbs"],
];

const MICROS: [Metric, string, string][] = [
    ["sodium", "Sodium", "mg"],
    ["cholesterol", "Cholesterol", "mg"],
    ["potassium", "Potassium", "mg"],
    ["magnesium", "Magnesium", "mg"],
    ["vitamin_a_dv", "Vitamin A", "IU"],
    ["vitamin_c_dv", "Vitamin C", "mg"],
    ["calcium_dv", "Calcium", "mg"],
    ["iron_dv", "Iron", "mg"],
];

const UNIT = "text-[oklch(0.6_0.02_280)]";

const Unit = ({children, size}: {children: string; size: string}) => (
    <span className={classNames(UNIT, size)}>{" " + children}</span>
);

const Donut = ({summary}: {summary: Calculations["summary"]}) => {
    const pct = (value: number) => (Number.isNaN(value) ? 0 : value);
    const protein = pct(summary.protein.percentage);
    const carbs = pct(summary.carbs.percentage);
    const fat = pct(summary.fat.percentage);
    return (
        <div
            className={classNames(
                "flex items-center gap-4 border-t pt-3 pb-2.5",
                HAIRLINE,
            )}
        >
            <div
                className="grid size-11 flex-none place-items-center rounded-full"
                style={{
                    background:
                        protein + carbs + fat === 0
                            ? "oklch(0.35 0.04 280 / 0.08)"
                            : `conic-gradient(oklch(0.51 0.21 278) 0 ${protein}%, oklch(0.74 0.11 278) ${protein}% ${protein + carbs}%, oklch(0.72 0.14 35) ${protein + carbs}%)`,
                }}
            >
                <div className="size-[30px] rounded-full bg-white" />
            </div>
            <div className="flex flex-1 justify-between">
                {(
                    [
                        ["Protein", protein],
                        ["Carbs", carbs],
                        ["Fat", fat],
                    ] as const
                ).map(([label, value]) => (
                    <span key={label}>
                        <span className="block text-[11px] text-[oklch(0.5_0.03_280)]">
                            {label}
                        </span>
                        <span className="text-[14.5px] font-medium">{value}%</span>
                    </span>
                ))}
            </div>
        </div>
    );
};

// The 31 nutrition card: energy over a dark hairline, the three macros, the
// sub-macros, the micros two up, and the calorie split.
export const NutritionCard = ({
    calculations,
    useKilojoules,
    eyebrow,
}: {
    calculations: Calculations;
    useKilojoules: boolean;
    eyebrow: string;
}) => {
    const subMacros = SUB_MACROS.filter(([metric]) => calculations[metric] != null);
    const micros = MICROS.filter(([metric]) => calculations[metric] != null);
    return (
        <div className="rounded-[14px] bg-white px-4 pt-4 pb-1.5 text-[oklch(0.24_0.02_280)]">
            <div className="flex items-end justify-between border-b border-[oklch(0.25_0.02_280)] pb-3">
                <span className="text-[34px] leading-none font-extralight tracking-[-0.03em]">
                    {calculations.calories ?? "0"}{" "}
                    <span className="text-[12px] font-normal tracking-normal text-[oklch(0.55_0.03_280)]">
                        {useKilojoules ? "kJ" : "kcal"}
                    </span>
                </span>
                <span className={EYEBROW}>{eyebrow}</span>
            </div>
            <div className={classNames("flex border-b py-3.5", HAIRLINE)}>
                {(
                    [
                        ["protein", "Protein"],
                        ["fat", "Fat"],
                        ["carbs", "Carbs"],
                    ] as const
                ).map(([metric, label], index) => (
                    <div
                        key={metric}
                        className={classNames(
                            "flex-1 text-center",
                            index < 2 && classNames("border-r", HAIRLINE),
                        )}
                    >
                        <div className="text-[21px] font-light">
                            {calculations[metric] ?? "0"}
                            <Unit size="text-[12px]">g</Unit>
                        </div>
                        <div className={classNames(EYEBROW, "mt-[3px]")}>{label}</div>
                    </div>
                ))}
            </div>
            {subMacros.length > 0 && (
                <div className={classNames("border-b pt-2 pb-1.5", HAIRLINE)}>
                    {subMacros.map(([metric, label]) => (
                        <div
                            key={metric}
                            className="flex justify-between px-0.5 py-1 text-[13px]"
                        >
                            <span className="font-light text-[oklch(0.42_0.03_280)]">
                                {label}
                            </span>
                            <span>
                                {calculations[metric]}
                                <Unit size="text-[11px]">g</Unit>
                            </span>
                        </div>
                    ))}
                </div>
            )}
            {micros.length > 0 && (
                <div className="grid grid-cols-2 gap-x-[18px] pt-2 pb-1.5 text-[12.5px]">
                    {micros.map(([metric, label, unit]) => (
                        <div
                            key={metric}
                            className="flex justify-between px-0.5 py-[3px]"
                        >
                            <span className="font-light text-[oklch(0.5_0.03_280)]">
                                {label}
                            </span>
                            <span>
                                {calculations[metric]}
                                <Unit size="text-[10.5px]">{unit}</Unit>
                            </span>
                        </div>
                    ))}
                </div>
            )}
            <Donut summary={calculations.summary} />
        </div>
    );
};
